import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel as FormControlLabelBase,
  Radio,
  RadioGroup,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Switch
} from '@mui/material';
import {
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  Preview as PreviewIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Grade as GradeIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  Sms as SmsIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { getSchoolExams, getExamStudents, sendGradeDetails } from '../services/communicationsService';
import api from '../api/api';

interface Student {
  student_id: number;
  student_name: string;
  student_code: string;
  class_level: string;
  score: number;
  percentage: number;
  rank: number;
  grade_letter: string;
  status: string;
  cbc_level: string;
  parent_email: string | null;
  parent_phone: string | null;
  has_complete_data: boolean;
}

interface Exam {
  id: number;
  name: string;
  class_level: string;
  subject: string;
  exam_date: string;
  total_marks: number;
  passing_marks: number;
}

const GradeDetailsSender: React.FC = () => {
  const [exams, setExams] = useState<Exam[]>([]);
  const [selectedExam, setSelectedExam] = useState<number | ''>('');
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Targeting options
  const [targetType, setTargetType] = useState<'all' | 'class' | 'specific_students'>('all');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  
  // Communication options
  const [enableEmail, setEnableEmail] = useState(true);
  const [enableWhatsApp, setEnableWhatsApp] = useState(false);
  const [enableSMS, setEnableSMS] = useState(false);
  const [smsConfigStatus, setSmsConfigStatus] = useState<any>(null);
  const [customMessage, setCustomMessage] = useState('');
  
  // Preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [filteredClass, setFilteredClass] = useState<string>('');

  useEffect(() => {
    fetchExams();
    checkSmsConfiguration();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      fetchStudents();
    } else {
      setStudents([]);
    }
  }, [selectedExam]);

  const checkSmsConfiguration = async () => {
    try {
      const response = await api.get('/settings/value/sms_enabled');
      const smsEnabled = (response.data as any).value === 'true';
      setEnableSMS(smsEnabled);
      
      if (smsEnabled) {
        // Get SMS configuration status
        const configResponse = await api.get('/settings/value/sms_provider');
        setSmsConfigStatus({
          enabled: smsEnabled,
          provider: (configResponse.data as any).value
        });
      }
    } catch (error) {
      console.error('Error checking SMS configuration:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const data = await getSchoolExams();
      setExams(data as Exam[]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to fetch exams');
    }
  };

  const fetchStudents = async () => {
    if (!selectedExam) return;
    
    setLoading(true);
    setError('');
    try {
      const data = await getExamStudents(selectedExam as number);
      setStudents(data as Student[]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelection = (studentId: number) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.student_id));
    }
  };

  const getTargetedStudents = () => {
    if (targetType === 'all') {
      return students;
    } else if (targetType === 'class') {
      return students.filter(s => s.class_level === selectedClass);
    } else {
      return students.filter(s => selectedStudents.includes(s.student_id));
    }
  };

  const getDisplayStudents = () => {
    if (targetType === 'all') {
      return students;
    } else if (targetType === 'class') {
      return students.filter(s => s.class_level === selectedClass);
    } else {
      // For specific students, show all students in the table but highlight selected ones
      return students;
    }
  };

  // Add this function to get students filtered by class
  const getFilteredStudents = () => {
    if (!filteredClass || filteredClass === 'ALL') return getDisplayStudents();
    return getDisplayStudents().filter(s => s.class_level === filteredClass);
  };

  const getCBCLevelColor = (level: string) => {
    switch (level) {
      case 'Exceeds Expectation': return 'success';
      case 'Meets Expectation': return 'info';
      case 'Approaches Expectation': return 'warning';
      case 'Below Expectation': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'passed' ? 'success' : 'error';
  };

  const handlePreview = () => {
    const targetedStudents = getTargetedStudents();
    const exam = exams.find(e => e.id === selectedExam);
    
    if (!exam || targetedStudents.length === 0) {
      setError('Please select an exam and at least one student');
      return;
    }

    setPreviewData({
      exam,
      students: targetedStudents,
      targetType,
      selectedClass,
      enableEmail,
      enableWhatsApp,
      enableSMS,
      customMessage
    });
    setShowPreview(true);
  };

  const handleSend = async () => {
    const targetedStudents = getTargetedStudents();
    const exam = exams.find(e => e.id === selectedExam);
    
    if (!exam || targetedStudents.length === 0) {
      setError('Please select an exam and at least one student');
      return;
    }

    if (!enableEmail && !enableWhatsApp && !enableSMS) {
      setError('Please enable at least one communication method');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const requestData = {
        exam_id: selectedExam,
        target_type: targetType,
        class_level: targetType === 'class' ? selectedClass : undefined,
        student_ids: targetType === 'specific_students' ? selectedStudents : undefined,
        enable_email: enableEmail,
        enable_whatsapp: enableWhatsApp,
        enable_sms: enableSMS,
        custom_message: customMessage
      };

      const response = await sendGradeDetails(requestData);
      
      setSuccess(`Exam reports sent successfully to ${targetedStudents.length} parents`);
      setSelectedStudents([]);
      setCustomMessage('');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to send exam reports');
    } finally {
      setSending(false);
    }
  };

  const targetedStudents = getTargetedStudents();
  const availableClasses = Array.from(new Set(students.map(s => s.class_level)));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <GradeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Exam Report Sender
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Send comprehensive exam reports to parents with total scores, rankings, and CBC indicators.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Exam Selection */}
        <Box sx={{ flex: '1 1 400px', minWidth: 0 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Select Exam
              </Typography>
              {/* Class Level Filter Dropdown (now inside the card) */}
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Filter by Class</InputLabel>
                  <Select
                    value={filteredClass}
                    onChange={e => setFilteredClass(e.target.value)}
                    label="Filter by Class"
                  >
                    <MenuItem value="ALL">All Classes</MenuItem>
                    {availableClasses.map((classLevel) => (
                      <MenuItem key={classLevel} value={classLevel}>{classLevel}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Exam</InputLabel>
                <Select
                  value={selectedExam}
                  onChange={(e) => setSelectedExam(e.target.value)}
                  label="Exam"
                >
                  {exams
                    .filter(exam => !filteredClass || filteredClass === 'ALL' || exam.class_level === filteredClass)
                    .map((exam) => (
                      <MenuItem key={exam.id} value={exam.id}>
                        {exam.name} - {exam.class_level} ({exam.exam_date})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {selectedExam && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Students: {students.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Exam Date: {exams.find(e => e.id === selectedExam)?.exam_date}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Targeting Options */}
        <Box sx={{ flex: '1 1 400px', minWidth: 0 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Targeting Options
              </Typography>
              
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend">Target Type</FormLabel>
                <RadioGroup
                  value={targetType}
                  onChange={(e) => setTargetType(e.target.value as any)}
                >
                  <FormControlLabel value="all" control={<Radio />} label="All Students" />
                  <FormControlLabel value="class" control={<Radio />} label="Specific Class" />
                  <FormControlLabel value="specific_students" control={<Radio />} label="Specific Students" />
                </RadioGroup>
              </FormControl>

              {targetType === 'class' && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Class Level</InputLabel>
                  <Select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    label="Class Level"
                  >
                    {availableClasses.map((classLevel) => (
                      <MenuItem key={classLevel} value={classLevel}>
                        {classLevel}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {targetType === 'specific_students' && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedStudents.length === students.length}
                        indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                        onChange={handleSelectAll}
                      />
                    }
                    label="Select All"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Selected: {selectedStudents.length} students
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Communication Options */}
        <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Communication Options
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableEmail}
                        onChange={(e) => setEnableEmail(e.target.checked)}
                      />
                    }
                    label="Send Email"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableWhatsApp}
                        onChange={(e) => setEnableWhatsApp(e.target.checked)}
                      />
                    }
                    label="Send WhatsApp"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableSMS}
                        onChange={(e) => setEnableSMS(e.target.checked)}
                        disabled={!smsConfigStatus?.enabled}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmsIcon />
                        Send SMS
                        {!smsConfigStatus?.enabled && (
                          <Tooltip title="SMS is not configured. Configure it in System Settings.">
                            <ErrorIcon color="warning" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                </Box>
                
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Custom Message (Optional)"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Add a personal message to include in the report..."
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Students Table */}
        {selectedExam && (
          <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Student Performance Data
                  </Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={handlePreview}
                      sx={{ mr: 1 }}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleSend}
                      disabled={sending || targetedStudents.length === 0}
                    >
                      {sending ? <CircularProgress size={20} /> : 'Send Reports'}
                    </Button>
                  </Box>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {targetType === 'specific_students' && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedStudents.length === students.length}
                                indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                                onChange={handleSelectAll}
                              />
                            </TableCell>
                          )}
                          <TableCell>Rank</TableCell>
                          <TableCell>Student Name</TableCell>
                          <TableCell>Student ID</TableCell>
                          <TableCell>Class</TableCell>
                          <TableCell>Total Score</TableCell>
                          <TableCell>Percentage</TableCell>
                          <TableCell>CBC Level</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Contact Info</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getFilteredStudents().map((student) => (
                          <TableRow 
                            key={student.student_id}
                            sx={{
                              backgroundColor: targetType === 'specific_students' && selectedStudents.includes(student.student_id) 
                                ? 'rgba(25, 118, 210, 0.08)' 
                                : 'inherit',
                              '&:hover': {
                                backgroundColor: targetType === 'specific_students' && selectedStudents.includes(student.student_id)
                                  ? 'rgba(25, 118, 210, 0.12)'
                                  : 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            {targetType === 'specific_students' && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedStudents.includes(student.student_id)}
                                  onChange={() => handleStudentSelection(student.student_id)}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <Chip 
                                label={student.rank} 
                                color="primary" 
                                size="small"
                                icon={<TrophyIcon />}
                              />
                            </TableCell>
                            <TableCell>{student.student_name}</TableCell>
                            <TableCell>{student.student_code}</TableCell>
                            <TableCell>{student.class_level}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {student.score.toFixed(1)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {student.percentage.toFixed(1)}%
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={student.cbc_level} 
                                color={getCBCLevelColor(student.cbc_level) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={student.status} 
                                color={getStatusColor(student.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box>
                                {student.parent_email && (
                                  <Typography variant="caption" display="block">
                                    📧 {student.parent_email}
                                  </Typography>
                                )}
                                {student.parent_phone && (
                                  <Typography variant="caption" display="block">
                                    📱 {student.parent_phone}
                                  </Typography>
                                )}
                                {!student.parent_email && !student.parent_phone && (
                                  <Typography variant="caption" color="error">
                                    No contact info
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {targetedStudents.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      📊 Summary: {targetedStudents.length} students selected for report sending
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      📧 Email: {targetedStudents.filter(s => s.parent_email).length} students
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      📱 WhatsApp: {targetedStudents.filter(s => s.parent_phone).length} students
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      📱 SMS: {targetedStudents.filter(s => s.parent_phone).length} students
                    </Typography>
                  </Box>
                )}

                {targetType === 'specific_students' && selectedStudents.length === 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="warning.dark">
                      ⚠️ Please select at least one student to send reports
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {/* Preview Dialog would go here */}
    </Box>
  );
};

export default GradeDetailsSender; 